import { useState, useMemo } from "react";
import {
  Building2,
  TrendingUp,
  TrendingDown,
  Landmark,
  Lock,
  Pencil,
  Wallet,
  RotateCcw,
  Download,
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
  Coins,
} from "lucide-react";
import StatCard from "../components/StatCard";
import {
  TEAMS,
  METHODS,
  summaryForFilter,
  transactionsPage,
  formatTRY,
  formatInt,
  exportCSV,
  DATE_MIN,
  DATE_MAX,
} from "../data/mock";

const StatusBadge = ({ s }) => {
  const map = {
    Onaylandı: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
    Beklemede: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    Reddedildi: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  };
  return <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${map[s]}`}>{s}</span>;
};

const Select = ({ label, value, onChange, children, testid }) => (
  <div className="relative">
    <label className="absolute -top-2 left-3 px-1 bg-white dark:bg-[#151824] text-[11px] font-semibold text-slate-500 z-10">
      {label}
    </label>
    <select
      value={value}
      onChange={onChange}
      data-testid={testid}
      className="w-full appearance-none rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-[#151824] px-4 py-2.5 pr-9 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-orange-500/40 cursor-pointer"
    >
      {children}
    </select>
    <ChevronRight size={16} className="absolute right-3 top-1/2 -translate-y-1/2 rotate-90 text-slate-400 pointer-events-none" />
  </div>
);

export default function KasaBilgisi() {
  const [teamId, setTeamId] = useState("SRL201");
  const [method, setMethod] = useState("all");
  const [refresh, setRefresh] = useState("Kapalı");
  const [from, setFrom] = useState(DATE_MIN);
  const [to, setTo] = useState(DATE_MAX);
  const [tab, setTab] = useState("all");
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [blokeEdit, setBlokeEdit] = useState(false);
  const [blokeOverride, setBlokeOverride] = useState(null);

  const summary = useMemo(() => summaryForFilter({ teamId, method, from, to }), [teamId, method, from, to]);
  const bloke = blokeOverride ?? summary.bloke;

  const { rows: pageRows, total } = useMemo(
    () => transactionsPage({ teamId, method, type: tab, from, to }, page, perPage),
    [teamId, method, tab, from, to, page, perPage]
  );
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const reset = () => {
    setTeamId("SRL201");
    setMethod("all");
    setFrom(DATE_MIN);
    setTo(DATE_MAX);
    setTab("all");
    setPage(0);
    setBlokeOverride(null);
  };

  const cards = [
    { title: "Toplam Yatırım", value: formatTRY(summary.yatirim), color: "#78C043", icon: TrendingUp, testid: "card-yatirim" },
    { title: "Toplam Çekim", value: formatTRY(summary.cekim), color: "#F26063", icon: TrendingDown, testid: "card-cekim" },
    { title: "Toplam Komisyon", value: formatTRY(summary.komisyon), color: "#F5C24C", icon: Coins, testid: "card-komisyon" },
    { title: "Toplam Bakiye", value: formatTRY(summary.bakiye), color: "#F0813E", icon: Landmark, testid: "card-bakiye" },
  ];

  return (
    <div className="space-y-5" data-testid="page-kasa-bilgisi">
      {/* Filter bar */}
      <div className="bg-white dark:bg-[#151824] rounded-2xl p-4 sm:p-5 shadow-sm border border-slate-100 dark:border-slate-800">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex items-center gap-2.5 shrink-0">
            <div className="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
              <Building2 className="text-[#F0651E]" size={22} />
            </div>
            <span className="text-xl font-extrabold text-[#F0651E]">Kasa Bilgisi</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 flex-1">
            <Select label="Saha Ekibi" value={teamId} onChange={(e) => { setTeamId(e.target.value); setPage(0); }} testid="filter-team">
              <option value="all">Tümü</option>
              {TEAMS.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </Select>
            <Select label="Ödeme Yöntemi" value={method} onChange={(e) => { setMethod(e.target.value); setPage(0); }} testid="filter-method">
              <option value="all">Tümü</option>
              {METHODS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </Select>
            <Select label="Yenileme Aralığı" value={refresh} onChange={(e) => setRefresh(e.target.value)} testid="filter-refresh">
              <option>Kapalı</option>
              <option>30 sn</option>
              <option>60 sn</option>
            </Select>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={reset} data-testid="btn-temizle" className="flex items-center gap-2 rounded-xl border border-slate-300 dark:border-slate-700 px-4 py-2.5 text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-800">
              <RotateCcw size={16} /> TEMİZLE
            </button>
            <button
              onClick={() => exportCSV(transactionsPage({ teamId, method, type: tab, from, to }, 0, Math.min(total, 1000)).rows.map((r) => ({ BayiID: r.bayiId, Musteri: r.musteriAdi, Tur: r.type, Yontem: r.method, Miktar: r.amount, Banka: r.bank, Durum: r.status, Tarih: r.dateStr })), "kasa-bilgisi.csv")}
              data-testid="btn-excel"
              className="flex items-center gap-2 rounded-xl bg-[#F0651E] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#d9560f] shadow-md shadow-orange-500/25"
            >
              <Download size={16} /> EXCEL
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <div className="relative">
            <label className="absolute -top-2 left-3 px-1 bg-white dark:bg-[#151824] text-[11px] font-semibold text-slate-500 z-10">Başlangıç Tarihi</label>
            <input type="date" min={DATE_MIN} max={DATE_MAX} value={from} onChange={(e) => { setFrom(e.target.value); setPage(0); }} data-testid="filter-from" className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-[#151824] px-4 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-orange-500/40" />
          </div>
          <div className="relative">
            <label className="absolute -top-2 left-3 px-1 bg-white dark:bg-[#151824] text-[11px] font-semibold text-slate-500 z-10">Bitiş Tarihi</label>
            <input type="date" min={DATE_MIN} max={DATE_MAX} value={to} onChange={(e) => { setTo(e.target.value); setPage(0); }} data-testid="filter-to" className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-[#151824] px-4 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-orange-500/40" />
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4">
        {cards.map((c) => (
          <StatCard key={c.title} {...c} />
        ))}
        <StatCard
          title="Bloke"
          value={blokeEdit ? (
            <input
              autoFocus
              type="number"
              defaultValue={bloke}
              onBlur={(e) => { setBlokeOverride(Number(e.target.value)); setBlokeEdit(false); }}
              onKeyDown={(e) => { if (e.key === "Enter") { setBlokeOverride(Number(e.target.value)); setBlokeEdit(false); } }}
              className="w-full bg-white/70 rounded-md px-2 py-0.5 text-black text-lg font-bold outline-none"
              data-testid="bloke-input"
            />
          ) : formatTRY(bloke)}
          color="#7A2FBE"
          icon={Lock}
          testid="card-bloke"
          actions={
            <button onClick={() => setBlokeEdit(true)} data-testid="btn-bloke-edit" className="hover:scale-110 transition-transform">
              <Pencil size={16} />
            </button>
          }
        />
        <StatCard title="Kullanılabilir Bakiye" value={formatTRY(summary.bakiye - bloke)} color="#1E7A5E" icon={Wallet} testid="card-kullanilabilir" />
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-[#151824] rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 px-4 sm:px-6">
          <div className="flex gap-1 sm:gap-6 overflow-x-auto">
            {[
              { k: "all", label: "TÜM İŞLEMLER", icon: null },
              { k: "yatirim", label: "YATIRIMLAR", icon: TrendingUp },
              { k: "cekim", label: "ÇEKİMLER", icon: TrendingDown },
            ].map((t) => (
              <button
                key={t.k}
                onClick={() => { setTab(t.k); setPage(0); }}
                data-testid={`tab-${t.k}`}
                className={`flex items-center gap-1.5 py-4 text-sm font-bold whitespace-nowrap border-b-2 transition-colors ${
                  tab === t.k ? "border-[#F0651E] text-[#F0651E]" : "border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                }`}
              >
                {t.icon && <t.icon size={16} className={t.k === "yatirim" ? "text-green-500" : "text-red-500"} />}
                {t.label}
              </button>
            ))}
          </div>
          <button className="hidden sm:flex items-center gap-2 rounded-xl border border-slate-300 dark:border-slate-700 px-4 py-2 text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-800" data-testid="btn-filtrele">
            <SlidersHorizontal size={16} /> FİLTRELE
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[13px] font-bold text-violet-600 dark:text-violet-400">
                <th className="px-4 sm:px-6 py-3">KULLANICI ID</th>
                <th className="px-4 py-3">KULLANICI ADI</th>
                <th className="px-4 py-3">İŞLEM TÜRÜ</th>
                <th className="px-4 py-3">YÖNTEM</th>
                <th className="px-4 py-3 text-right">MİKTAR</th>
                <th className="px-4 py-3">BANKA</th>
                <th className="px-4 py-3">DURUM</th>
                <th className="px-4 sm:px-6 py-3 text-right">TARİH</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {pageRows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-16 text-slate-400 font-medium">Veri bulunamadı</td>
                </tr>
              ) : (
                pageRows.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid="tx-row">
                    <td className="px-4 sm:px-6 py-3 font-bold text-slate-700 dark:text-slate-200">{r.bayiId}</td>
                    <td className="px-4 py-3">{r.musteriAdi}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 font-semibold ${r.type === "yatirim" ? "text-green-600" : "text-red-500"}`}>
                        {r.type === "yatirim" ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                        {r.type === "yatirim" ? "Yatırım" : "Çekim"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{r.method}</td>
                    <td className={`px-4 py-3 text-right font-bold ${r.type === "yatirim" ? "text-green-600" : "text-red-500"}`}>
                      {r.type === "yatirim" ? "+" : "-"}{formatTRY(r.amount)}
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{r.bank}</td>
                    <td className="px-4 py-3"><StatusBadge s={r.status} /></td>
                    <td className="px-4 sm:px-6 py-3 text-right text-slate-500 whitespace-nowrap">{r.dateStr}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 sm:px-6 py-4 border-t border-slate-100 dark:border-slate-800 text-sm">
          <div className="flex items-center gap-2 text-slate-500">
            Sayfa başına satır
            <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setPage(0); }} data-testid="per-page" className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-[#151824] px-2 py-1 font-medium">
              {[10, 25, 50, 100].map((n) => (<option key={n} value={n}>{n}</option>))}
            </select>
          </div>
          <div className="flex items-center gap-4 text-slate-500">
            <span>Toplam: {formatInt(total)} işlem · {total ? page * perPage + 1 : 0}–{page * perPage + pageRows.length}</span>
            <div className="flex items-center gap-1">
              <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="prev-page" className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30">
                <ChevronLeft size={18} />
              </button>
              <span className="font-semibold text-slate-700 dark:text-slate-200">{page + 1} / {totalPages}</span>
              <button disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="next-page" className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30">
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
